import React, { ReactElement, useEffect } from 'react';
import { useDeleteEntite, useEntites } from '../../../Utils/api';
import { Fournisseur } from '../../../../../common/src/entities';
import TableSimple from '../Utils/TableSimple';
import { Badge, Button, ButtonGroup, Col, Dropdown, Row } from 'react-bootstrap';
import { CellProps } from 'react-table';
import FiltreActif from '../Utils/FiltreActif';
import { LinkContainer } from 'react-router-bootstrap';
import { BiFile, BiNote, BiPlus } from 'react-icons/all';
import EnteteDePage from '../Utils/EnteteDePage';
import { useNavigate } from 'react-router-dom';
import { useAlertDialog } from '../../../Utils/dialogContext';
import PopoverSimple from '../../Common/PopoverSimple';
import { CelluleContact } from '../Utils/CelluleContact';
import { DateTime } from 'luxon';
import { capitalize } from '../../../Utils/stringUtils';
import { Colonne } from '../Utils/TypesTableSimple';

export default function ListeFournisseurs(): ReactElement {
    const bsAlert = useAlertDialog();
    const navigate = useNavigate();
    const listFournisseurs = useEntites(Fournisseur);
    const [supprimerFournisseurQuery] = useDeleteEntite(Fournisseur);

    useEffect(() => {
        document.title = 'Fournisseurs - Dashboard';
    }, []);

    const colonnes: Colonne<Fournisseur>[] = [
        {
            Header: 'Actif',
            accessor: 'actif',
            Cell: function CelluleActif({ value }: CellProps<Fournisseur>) {
                return value ? <Badge variant="light">Actif</Badge> : <Badge variant="secondary">Inactif</Badge>;
            },
            filter: 'equals',
        },
        {
            Header: 'Nom',
            accessor: 'name',
            sortType: (rowA, rowB) => {
                return rowA.original.name.localeCompare(rowB.original.name, 'fr-CA');
            },
        },
        {
            Header: 'Notes',
            Cell: function CelluleNotes({ value }: CellProps<Fournisseur>) {
                return value ? (
                    <PopoverSimple inList texte={value} titre="Notes">
                        <BiNote />
                    </PopoverSimple>
                ) : null;
            },
            accessor: 'notes',
            disableSortBy: true,
        },
        {
            Header: 'Suivi',
            Cell: function CelluleSuivi({ value }: CellProps<Fournisseur>) {
                if (value) {
                    const date = DateTime.fromISO(value);
                    const isPast = date.diffNow('days').days < 0;
                    return (
                        <Badge variant={isPast ? 'warning' : 'secondary'}>
                            {isPast ? `Dû ${date.toRelativeCalendar()}` : capitalize(date.toRelativeCalendar())}
                        </Badge>
                    );
                }
                return <Badge variant="success">OK</Badge>;
            },
            accessor: 'trigger_suivi',
            sortType: (rowA, rowB) => {
                const dateA = rowA.original.trigger_suivi ? DateTime.fromISO(rowA.original.trigger_suivi) : null;
                const dateB = rowB.original.trigger_suivi ? DateTime.fromISO(rowB.original.trigger_suivi) : null;
                if (dateA == null) return 1;
                if (dateB == null) return -1;
                return dateA.diff(dateB, 'days').days > 0 ? 1 : -1;
            },
        },
        {
            Cell: ({ row }) =>
                CelluleContact({ value: row.original.contact_par_defaut ?? row.original.contact_gestionnaire }),
            Header: 'Contact',
        },
    ];

    const deleteFournisseur = async (f: Fournisseur) => {
        if (window.confirm('Etes-vous sur de vouloir supprimer ce fournisseur?')) {
            try {
                await supprimerFournisseurQuery(f.id);
            } catch (error) {
                const { response } = error;
                if (response.status === 409) {
                    bsAlert("D'autres entités dépendent de ce fournisseur, impossible de le supprimer", 'warning');
                } else {
                    bsAlert(`Erreur dans la suppression du fournisseur: ${error.message}`, 'danger');
                }
            }
        }
    };

    return (
        <>
            <Row>
                <Col>
                    <EnteteDePage texte="Fournisseurs" cacherBoutonRetour />
                </Col>
                <Col xs="auto">
                    <ButtonGroup>
                        <LinkContainer to="new">
                            <Button size="sm" variant="outline-primary" className="d-flex align-items-center">
                                <BiPlus /> Ajouter un fournisseur
                            </Button>
                        </LinkContainer>
                        <Button size="sm" variant="outline-secondary" disabled className="d-flex align-items-center">
                            <BiFile /> Exporter en CSV
                        </Button>
                    </ButtonGroup>
                </Col>
            </Row>
            <TableSimple
                colonnes={colonnes}
                resultat={listFournisseurs}
                lien="id"
                hover
                affichageMenu={(fournisseur: Fournisseur) => (
                    <>
                        <Dropdown.Item onClick={() => navigate(String(fournisseur.id))}>Modifier</Dropdown.Item>
                        <Dropdown.Item onClick={() => deleteFournisseur(fournisseur)} className="text-danger">
                            Supprimer
                        </Dropdown.Item>
                    </>
                )}
                affichageFiltres={FiltreActif}
                etatInitial={{
                    filters: [
                        {
                            id: 'actif',
                            value: true,
                        },
                    ],
                    sortBy: [
                        {
                            id: 'name',
                        },
                    ],
                    hiddenColumns: ['actif'],
                }}
            />
        </>
    );
}
